// Pagination component
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
}

export default function Pagination({ currentPage, totalPages, total, limit, onPageChange, onLimitChange }: PaginationProps) {
  const startItem = totalPages === 0 ? 0 : (currentPage - 1) * limit + 1;
  const endItem = Math.min(currentPage * limit, total);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      
      if (currentPage > 3) pages.push('...');
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) pages.push(i);
      
      if (currentPage < totalPages - 2) pages.push('...');
      
      pages.push(totalPages);
    }

    return pages;
  };

  if (totalPages <= 1 && !onLimitChange) return null;

  return (
    <div className="flex items-center justify-between gap-4 mt-6 flex-wrap">
      {/* Info */}
      <div className="text-text-muted text-sm">
        Showing {startItem}–{endItem} of {total}
      </div>

      {/* Page controls */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 text-sm rounded border border-border bg-surface hover:bg-surface-hover text-text-secondary disabled:opacity-40 disabled:cursor-not-allowed min-h-[36px]"
          aria-label="Previous page"
        >
          ← Prev
        </button>

        {getPageNumbers().map((page, idx) =>
          typeof page === 'number' ? (
            <button
              key={idx}
              onClick={() => onPageChange(page)}
              className={`px-3 py-1 text-sm rounded border min-h-[36px] min-w-[36px] ${
                page === currentPage
                  ? 'bg-brand text-white border-brand'
                  : 'border-border bg-surface hover:bg-surface-hover text-text-secondary'
              }`}
            >
              {page}
            </button>
          ) : (
            <span key={idx} className="px-2 text-text-muted">
              {page}
            </span>
          )
        )}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 text-sm rounded border border-border bg-surface hover:bg-surface-hover text-text-secondary disabled:opacity-40 disabled:cursor-not-allowed min-h-[36px]"
          aria-label="Next page"
        >
          Next →
        </button>
      </div>

      {/* Items per page (optional) */}
      {onLimitChange && (
        <div className="flex items-center gap-2">
          <label className="text-text-muted text-sm">Per page:</label>
          <select
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
            className="bg-bg-primary text-text-primary border border-border rounded px-2 py-1 text-sm min-h-[36px]"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      )}
    </div>
  );
}
